import { useCallback } from 'react';
import { create } from 'zustand';
import { ConfirmDialog } from '@micarahr/web-ui';

/**
 * "Are you sure?" before every action that changes a record.
 *
 * One dialog for the whole console, asked for with `useConfirm()` and awaited
 * like any other step: `if (await confirm({...})) save()`. A dialog per page
 * meant each screen decided for itself whether an approval deserved a second
 * look, and most decided it did not — an approval, a rejection or a payroll
 * transition went out on a single click.
 *
 * The dialog names the action and the record it lands on. It carries no form
 * of its own: reasons and comments are typed on the page before the button is
 * pressed, and the dialog is the last look at what is about to happen.
 */

export interface ConfirmRequest {
  title: string;
  /** What will happen, in a sentence the person can check against the screen. */
  message: string;
  /** The button. Name the action ("Approve leave"), never "OK". */
  confirmLabel: string;
  /** `danger` for anything that rejects, removes, revokes or reopens. */
  tone?: 'primary' | 'danger';
}

interface ConfirmState {
  request: ConfirmRequest | null;
  resolve: ((confirmed: boolean) => void) | null;
  ask: (request: ConfirmRequest) => Promise<boolean>;
  settle: (confirmed: boolean) => void;
}

const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  resolve: null,
  ask: (request) =>
    new Promise<boolean>((resolve) => {
      // A second request while one is open answers the first with "no" rather
      // than leaving its caller waiting forever.
      get().resolve?.(false);
      set({ request, resolve });
    }),
  settle: (confirmed) => {
    get().resolve?.(confirmed);
    set({ request: null, resolve: null });
  },
}));

/** Resolves true when the person confirms, false when they cancel or dismiss. */
export function useConfirm(): (request: ConfirmRequest) => Promise<boolean> {
  const ask = useConfirmStore((state) => state.ask);
  return useCallback((request: ConfirmRequest) => ask(request), [ask]);
}

/** Rendered once, in the app shell. */
export function ConfirmHost() {
  const request = useConfirmStore((state) => state.request);
  const settle = useConfirmStore((state) => state.settle);

  return (
    <ConfirmDialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) settle(false);
      }}
      title={request?.title ?? ''}
      message={request?.message ?? ''}
      confirmLabel={request?.confirmLabel ?? 'Confirm'}
      tone={request?.tone ?? 'primary'}
      onConfirm={() => settle(true)}
    />
  );
}
